import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  GentleSupportIcon,
  LearningGrowthIcon,
  SupportCircleIcon,
} from "@/components/NavIcons";
import {
  Heart,
  MoreHorizontal,
  Palette,
  Flower2,
  CalendarDays,
  Search as SearchIcon,
  HelpCircle,
  Shield,
  Sparkles,
  LayoutDashboard,
} from "lucide-react";

import { useMembership } from "@/hooks/useMembership";
import { useMobileNavOpen } from "@/hooks/useMobileNavOpen";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

type Item = {
  to?: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  match?: (path: string) => boolean;
  more?: boolean;
};

const ITEMS: Item[] = [
  {
    to: "/",
    label: "Home",
    icon: Heart as unknown as React.ComponentType<React.SVGProps<SVGSVGElement>>,
    match: (p) => p === "/",
  },
  {
    to: "/my-healing-journey",
    label: "Start",
    icon: GentleSupportIcon,
    match: (p) =>
      p.startsWith("/my-healing-journey") ||
      p.startsWith("/how-to-start") ||
      p.startsWith("/") ||
      p.startsWith("/breathing") ||
      p.startsWith("/grounding") ||
      p.startsWith("/somatic") ||
      p.startsWith("/yoga") ||
      p.startsWith("/sounds") ||
      p.startsWith("/affirmations") ||
      p.startsWith("/bilateral") ||
      p.startsWith("/tapping") ||
      p.startsWith("/journal") ||
      p.startsWith("/dreams") ||
       p.startsWith("/dream-reading") ||
       p.startsWith("/dream-reflections") ||
      p.startsWith("/shadow-work") ||
      p.startsWith("/sleep-dreams") ||
      p.startsWith("/guided-journal") ||
      p.startsWith("/check-in") ||
      p.startsWith("/mood") ||
      p.startsWith("/favorites"),
  },
  {
    to: "/library",
    label: "Explore",
    icon: LearningGrowthIcon,
    match: (p) =>
      p.startsWith("/library") ||
      p.startsWith("/therapy-approaches") ||
      p.startsWith("/resources") ||
      p.startsWith("/courses") ||
      p.startsWith("/") ||
      p.startsWith("/roadmap") ||
      p.startsWith("/guide"),
  },

  {
    to: "/community",
    label: "Community",
    icon: SupportCircleIcon,
    match: (p) => p.startsWith("/community"),
  },
  {
    label: "More",
    icon: MoreHorizontal as unknown as React.ComponentType<React.SVGProps<SVGSVGElement>>,
    more: true,
  },
];

type MoreLink = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  emphasis?: boolean;
};

const MORE_LINKS: MoreLink[] = [
  { to: "/holistic-wellbeing", label: "Wellbeing Hub", icon: Flower2 },
  { to: "/", label: "Daily Support", icon: Sparkles },
  { to: "/events", label: "Live Events", icon: CalendarDays },
  { to: "/therapists", label: "Find a Therapist", icon: SearchIcon },
  { to: "/support", label: "Help & FAQ", icon: HelpCircle },

];

import { useTherapistOnlyMode } from "@/lib/launch-mode";

export function BottomNav() {
  const { pathname } = useLocation();
  const { isAdmin, isAuthed } = useMembership();
  const navOpen = useMobileNavOpen();
  const [moreOpen, setMoreOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const therapistOnly = useTherapistOnlyMode();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isTextInput = (el: Element | null) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "TEXTAREA") return true;
      if (tag === "INPUT") {
        const type = (el as HTMLInputElement).type;
        return !["button", "submit", "checkbox", "radio", "range", "file", "color"].includes(type);
      }
      return (el as HTMLElement).isContentEditable;
    };
    const onFocusIn = (e: FocusEvent) => {
      if (isTextInput(e.target as Element)) setKeyboardOpen(true);
    };
    const onFocusOut = () => setKeyboardOpen(false);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  if (therapistOnly) return null;
  if (navOpen) return null;
  if (keyboardOpen) return null;

  const memberHome: Item = {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard as unknown as React.ComponentType<React.SVGProps<SVGSVGElement>>,
    match: (p) => p.startsWith("/dashboard"),
  };

  const primaryItems = isAuthed ? [memberHome, ...ITEMS.slice(1)] : ITEMS;

  const items: Item[] = isAdmin
    ? [
        ...primaryItems.slice(0, 4),
        {
          to: "/admin",
          label: "Admin",
          icon: Shield as unknown as React.ComponentType<React.SVGProps<SVGSVGElement>>,
          match: (p) => p.startsWith("/admin"),
        },
        primaryItems[4],
      ]
    : primaryItems;

  return (
    <>
      <div aria-hidden className="h-14 lg:hidden" />
      <nav
        data-bottom-nav
        aria-label="Primary"
        className="fixed bottom-0 start-0 end-0 z-40 lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto max-w-[640px] px-1 pb-1.5 pt-1">
          <ul className="flex items-stretch justify-between gap-0 rounded-2xl border border-[var(--lavender)]/30 bg-background/75 px-1 py-1 shadow-[0_-2px_16px_-12px_rgba(107,90,122,0.2)] backdrop-blur-xl">
            {items.map((it) => {
              const Icon = it.icon;

              if (it.more) {
                return (
                  <li key="more" className="min-w-0 flex-1">
                    <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                      <SheetTrigger asChild>
                        <button
                          type="button"
                          aria-label="More navigation"
                          className="group relative flex w-full flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 transition-colors duration-300 hover:bg-[var(--icon-chip-bg)]"
                        >
                          <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-transparent group-hover:bg-[var(--icon-chip-bg)] transition-all duration-300">
                            <Icon className="h-4 w-4 text-[var(--icon-muted)]" />
                          </span>
                          <span className="block w-full truncate text-center text-[10px] font-medium tracking-tight text-[var(--icon-muted)] leading-none">
                            More
                          </span>
                        </button>
                      </SheetTrigger>
                      <SheetContent
                        side="bottom"
                        className="rounded-t-3xl border-gold/20 bg-background p-6"
                      >
                        <SheetHeader className="mb-4 text-start">
                          <SheetTitle className="font-display text-xl text-foreground">
                            More
                          </SheetTitle>
                        </SheetHeader>
                        <ul className="flex flex-col gap-1.5">
                          {MORE_LINKS.map((m) => (
                            <li key={m.to}>
                              <Link
                                to={m.to}
                                onClick={() => setMoreOpen(false)}
                                className="flex min-h-[56px] items-center gap-3 rounded-2xl border border-gold/15 bg-cream/40 px-4 py-3 text-base font-medium text-foreground/85 transition-colors hover:border-gold/40 hover:bg-gold-light/40 hover:text-[var(--gold-deep)]"
                              >
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]">
                                  <m.icon className="h-5 w-5" />
                                </span>
                                {m.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </SheetContent>
                    </Sheet>
                  </li>
                );
              }

              const active = it.match ? it.match(pathname) : pathname.startsWith(it.to!);

              return (
                <li key={it.to} className="min-w-0 flex-1">
                  <Link
                    to={it.to!}
                    activeOptions={it.to === "/" ? { exact: true } : undefined}
                    aria-current={active ? "page" : undefined}
                    className="group relative flex flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 transition-colors duration-300 hover:bg-[var(--icon-chip-bg)]"
                  >
                    <span
                      className={`relative flex h-7 w-7 items-center justify-center rounded-full transition-all duration-300 ${
                        active
                          ? "bg-[var(--icon-chip-active-bg)] scale-105"
                          : "bg-transparent group-hover:bg-[var(--icon-chip-bg)]"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 transition-colors duration-300 ${
                          active ? "text-[var(--icon-active)]" : "text-[var(--icon-muted)]"
                        }`}
                      />
                    </span>
                    <span
                      className={`block w-full truncate text-center text-[10px] font-medium tracking-tight leading-none transition-colors duration-300 ${
                        active ? "text-[var(--icon-active)]" : "text-[var(--icon-muted)]"
                      }`}
                    >
                      {it.label}
                    </span>
                    <span
                      aria-hidden
                      className={`absolute -top-0.5 h-1 w-1 rounded-full transition-all duration-300 ${
                        active ? "bg-[var(--icon-active)] opacity-100" : "opacity-0"
                      }`}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </>
  );
}
